import { CustomView, EntityIcon, WbNoData } from '@agilelab/plugin-wb-platform';
import { Box, IconButton, Typography, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { FC } from 'react';
import { useEditorPageContext } from '../context/useEditorPageContext';

export const DescriptorCustomViewCard: FC = () => {
  const theme = useTheme();
  const {
    parsedDescriptor,
    customViewPageIndex,
    setCustomViewPageIndex,
    descriptorStateWithComponents,
  } = useEditorPageContext();

  if (descriptorStateWithComponents.loading) {
    return (
      <Box
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <Skeleton variant="rect" height="3vh" width="60%" />
        <Skeleton variant="rect" height="3vh" width="80%" />
        <Skeleton variant="rect" height="10vh" />
        <Skeleton variant="rect" height="3vh" width="70%" />
        <Skeleton variant="rect" height="20vh" />
        <Skeleton variant="rect" height="3vh" width="80%" />
        <Skeleton variant="rect" height="15vh" />
      </Box>
    );
  }

  if (!parsedDescriptor) {
    return <WbNoData fillContainer text="No descriptor available" />;
  }

  const components: any[] = parsedDescriptor.components ?? [];
  const totalPages = 1 + components.length;

  const isSystemPage = customViewPageIndex === 0;
  const componentIndex = customViewPageIndex - 1;
  const currentComponent = isSystemPage ? null : components[componentIndex];

  const systemName = parsedDescriptor.name ?? 'System';
  const currentTitle = isSystemPage
    ? systemName
    : currentComponent?.name ?? `Component ${componentIndex + 1}`;
  const currentUseCaseTemplateId = isSystemPage
    ? parsedDescriptor.useCaseTemplateId ?? ''
    : currentComponent?.useCaseTemplateId ?? '';
  const currentKind = isSystemPage
    ? (parsedDescriptor.kind ?? '').toLowerCase()
    : (currentComponent?.kind ?? '').toLowerCase();

  const handlePrevious = () => {
    if (customViewPageIndex > 0) setCustomViewPageIndex(i => i - 1);
  };

  const handleNext = () => {
    if (customViewPageIndex < totalPages - 1)
      setCustomViewPageIndex(i => i + 1);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      style={{ minHeight: 0, overflow: 'hidden' }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{
          flexShrink: 0,
          padding: theme.spacing(1.5, 2, 1.5, 1),
        }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <EntityIcon
            useCaseTemplateId={currentUseCaseTemplateId}
            kind={currentKind}
            title={currentTitle}
            disableSystemForceDefault
          />
          <Typography
            variant="subtitle2"
            component="p"
            style={{ fontWeight: 'bold' }}
          >
            {currentTitle}
          </Typography>
        </Box>
        {totalPages > 1 && (
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              color="primary"
              onClick={handlePrevious}
              disabled={customViewPageIndex === 0}
              aria-label="Previous page"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="body2"
              style={{ minWidth: 48, textAlign: 'center', fontWeight: 700 }}
            >
              {`${customViewPageIndex + 1} / ${totalPages}`}
            </Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={handleNext}
              disabled={customViewPageIndex >= totalPages - 1}
              aria-label="Next page"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box flex={1} style={{ minHeight: 0, position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          {isSystemPage ? (
            <CustomView
              id="builder_descriptor_system_preview"
              typeId={parsedDescriptor.kind}
              templateId={parsedDescriptor.useCaseTemplateId}
              data={{ descriptor: parsedDescriptor }}
            />
          ) : (
            <CustomView
              id="builder_descriptor_component_preview"
              typeId={currentComponent?.kind}
              templateId={currentComponent?.useCaseTemplateId}
              data={{ component: currentComponent }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
